import React, { useEffect, useMemo, useState } from 'react'
import { Calendar, Card, Col, DatePicker, Row, Segmented, Spin, Statistic, Table, Tabs, Typography, message } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import type { Dayjs } from 'dayjs'
import dayjs from 'dayjs'
import { get } from '../lib/request'
import { AnalyticsData, AnalyticsMonthPoint, AnalyticsPoint, errMsg } from '../lib/api'
import MiniLineChart from './MiniLineChart'
import MiniBarChart from './MiniBarChart'

const { Text } = Typography

const COLOR_CARDS = '#2557A7'
const COLOR_OPENS = '#16A085'
const COLOR_VISITORS = '#C9A84C'

type ViewMode = 'line' | 'bar' | 'calendar'
type Granularity = 'day' | 'month'

/**
 * 工作台「分析数据」卡（dashboard_analytics_v1）
 * 数据源 GET /admin/dashboard/analytics（契约见 planning/dashboard-analytics-20261027-plan.md）。
 * 布局对齐设计稿 dashboard-analytics-card.html：
 * 顶部 RangePicker（默认近 30 天）+ Segmented（折线/柱状/日历）+ Segmented（按日/按月，日历模式隐藏）
 * → 三张总数卡（蓝/绿/金）→ 图例 → 视图区（折线/柱状/日历 fullCellRender，范围外日期格显示「—」）
 * → 底部 Tabs（日表/月表两张 antd Table）。
 * 口径说明：统计自功能上线日起算，上线前日期恒为 0（接口按范围内逐日补零返回）。
 */
const AnalyticsCard: React.FC = () => {
  const [range, setRange] = useState<[Dayjs, Dayjs]>([dayjs().subtract(29, 'day'), dayjs()])
  const [view, setView] = useState<ViewMode>('line')
  const [granularity, setGranularity] = useState<Granularity>('day')
  const [data, setData] = useState<AnalyticsData | null>(null)
  const [loading, setLoading] = useState(true)
  // 日历面板受控月（默认范围起点所在月）
  const [panelMonth, setPanelMonth] = useState<Dayjs>(dayjs().subtract(29, 'day'))

  useEffect(() => {
    const [from, to] = range
    setLoading(true)
    get<AnalyticsData>('/admin/dashboard/analytics', {
      params: { from: from.format('YYYY-MM-DD'), to: to.format('YYYY-MM-DD') },
    })
      .then(setData)
      .catch((err) => message.warning(errMsg(err, '分析数据加载失败')))
      .finally(() => setLoading(false))
  }, [range])

  const daily = useMemo(() => data?.daily ?? [], [data])
  const monthly = useMemo(() => data?.monthly ?? [], [data])
  const dailyByDate = useMemo(() => {
    const map = new Map<string, AnalyticsPoint>()
    for (const d of daily) map.set(d.date, d)
    return map
  }, [daily])

  const chartData: AnalyticsPoint[] | AnalyticsMonthPoint[] = granularity === 'day' ? daily : monthly
  const xKey: 'date' | 'month' = granularity === 'day' ? 'date' : 'month'

  const inRange = (d: Dayjs): boolean =>
    !d.isBefore(range[0], 'day') && !d.isAfter(range[1], 'day')

  /** 日历格：范围内显示当天 生成数/打开数，范围外显示「—」；跨月格淡化（仿 DutyShiftCalendar） */
  const fullCellRender = (date: Dayjs) => {
    const key = date.format('YYYY-MM-DD')
    const point = dailyByDate.get(key)
    const inside = inRange(date)
    const isToday = date.isSame(dayjs(), 'day')
    const inMonth = date.month() === panelMonth.month()
    return (
      <div
        style={{
          minHeight: 72,
          padding: '4px 6px',
          border: isToday ? `2px solid ${COLOR_CARDS}` : '1px solid #f0f0f0',
          borderRadius: 8,
          background: inMonth ? '#fff' : '#fafafa',
          overflow: 'hidden',
          opacity: inMonth ? 1 : 0.55,
        }}
      >
        <div
          style={{
            fontWeight: isToday ? 700 : 600,
            color: isToday ? COLOR_CARDS : inMonth ? '#333' : '#bbb',
            fontSize: 13,
            lineHeight: '18px',
          }}
        >
          {date.date()}
        </div>
        {inside ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2, marginTop: 3, fontSize: 11, lineHeight: '16px' }}>
            <span style={{ color: COLOR_CARDS }}>生成 {point?.cards ?? 0}</span>
            <span style={{ color: COLOR_OPENS }}>打开 {point?.opens ?? 0}</span>
          </div>
        ) : (
          <div style={{ marginTop: 8, color: '#c0c4cc', fontSize: 12 }}>—</div>
        )}
      </div>
    )
  }

  const dayColumns: ColumnsType<AnalyticsPoint> = [
    { title: '日期', dataIndex: 'date', key: 'date' },
    { title: '留位卡生成数', dataIndex: 'cards', key: 'cards', width: 120 },
    { title: '链接打开数', dataIndex: 'opens', key: 'opens', width: 120 },
    { title: '打开人数', dataIndex: 'visitors', key: 'visitors', width: 120 },
  ]
  const monthColumns: ColumnsType<AnalyticsMonthPoint> = [
    { title: '月份', dataIndex: 'month', key: 'month' },
    { title: '留位卡生成数', dataIndex: 'cards', key: 'cards', width: 120 },
    { title: '链接打开数', dataIndex: 'opens', key: 'opens', width: 120 },
    { title: '打开人数', dataIndex: 'visitors', key: 'visitors', width: 120 },
  ]

  const legend = (
    <div style={{ display: 'flex', gap: 16, fontSize: 12, color: '#4e5969', margin: '-6px 0 12px' }}>
      <span>
        <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 2, marginRight: 5, background: COLOR_CARDS }} />
        留位卡生成数
      </span>
      <span>
        <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 2, marginRight: 5, background: COLOR_OPENS }} />
        链接打开数
      </span>
    </div>
  )

  return (
    <Card
      title="分析数据"
      style={{ borderRadius: 12 }}
      extra={
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
          <DatePicker.RangePicker
            value={range}
            allowClear={false}
            onChange={(v) => {
              if (v && v[0] && v[1]) {
                const next: [Dayjs, Dayjs] = [v[0].startOf('day'), v[1].startOf('day')]
                setRange(next)
                setPanelMonth(next[0])
              }
            }}
          />
          <Segmented<ViewMode>
            value={view}
            onChange={setView}
            options={[
              { label: '折线图', value: 'line' },
              { label: '柱状图', value: 'bar' },
              { label: '日历', value: 'calendar' },
            ]}
          />
          {view !== 'calendar' && (
            <Segmented<Granularity>
              value={granularity}
              onChange={setGranularity}
              options={[
                { label: '按日', value: 'day' },
                { label: '按月', value: 'month' },
              ]}
            />
          )}
        </div>
      }
    >
      <Spin spinning={loading}>
        {/* 三张总数卡：蓝/绿/金 */}
        <Row gutter={[12, 12]} style={{ marginBottom: 14 }}>
          <Col xs={24} sm={8}>
            <div style={{ background: '#f7f8fa', borderRadius: 8, padding: '12px 16px' }}>
              <Statistic
                title={<span style={{ fontSize: 12, color: '#86909c' }}>范围内累计生成留位卡</span>}
                value={data?.totals.cards ?? 0}
                valueStyle={{ color: COLOR_CARDS, fontWeight: 700, fontSize: 26 }}
              />
            </div>
          </Col>
          <Col xs={24} sm={8}>
            <div style={{ background: '#f7f8fa', borderRadius: 8, padding: '12px 16px' }}>
              <Statistic
                title={<span style={{ fontSize: 12, color: '#86909c' }}>范围内累计打开次数</span>}
                value={data?.totals.opens ?? 0}
                valueStyle={{ color: COLOR_OPENS, fontWeight: 700, fontSize: 26 }}
              />
            </div>
          </Col>
          <Col xs={24} sm={8}>
            <div style={{ background: '#f7f8fa', borderRadius: 8, padding: '12px 16px' }}>
              <Statistic
                title={<span style={{ fontSize: 12, color: '#86909c' }}>范围内累计打开人数</span>}
                value={data?.totals.visitors ?? 0}
                valueStyle={{ color: COLOR_VISITORS, fontWeight: 700, fontSize: 26 }}
              />
            </div>
          </Col>
        </Row>

        {/* 视图区 */}
        {view === 'calendar' ? (
          <div style={{ marginBottom: 16 }}>
            <Calendar
              fullscreen
              value={panelMonth}
              onPanelChange={(d) => setPanelMonth(d)}
              onSelect={(d, info) => {
                // 仅响应头部月/年切换外的点选；点某天不改面板月（日历只读展示）
                if (info.source === 'date') setPanelMonth(d)
              }}
              fullCellRender={fullCellRender}
            />
            <Text type="secondary" style={{ fontSize: 12 }}>
              仅展示所选范围内日期；范围外日期显示「—」。统计自功能上线日起算，上线前日期为 0。
            </Text>
          </div>
        ) : (
          <>
            {legend}
            {chartData.length === 0 ? (
              <div style={{ height: 220, display: 'flex', alignItems: 'center', justifyContent: 'center', borderBottom: '1px solid #f0f0f0', marginBottom: 16 }}>
                <Text type="secondary">暂无数据</Text>
              </div>
            ) : view === 'line' ? (
              <MiniLineChart data={chartData} xKey={xKey} />
            ) : (
              <MiniBarChart data={chartData} xKey={xKey} />
            )}
          </>
        )}

        {/* 底部日表/月表 */}
        <Tabs
          defaultActiveKey="day"
          items={[
            {
              key: 'day',
              label: '日表',
              children: (
                <Table<AnalyticsPoint>
                  rowKey="date"
                  size="small"
                  dataSource={daily}
                  columns={dayColumns}
                  pagination={{ pageSize: 10, showSizeChanger: false }}
                  locale={{ emptyText: '暂无数据' }}
                />
              ),
            },
            {
              key: 'month',
              label: '月表',
              children: (
                <Table<AnalyticsMonthPoint>
                  rowKey="month"
                  size="small"
                  dataSource={monthly}
                  columns={monthColumns}
                  pagination={false}
                  locale={{ emptyText: '暂无数据' }}
                />
              ),
            },
          ]}
        />
      </Spin>
    </Card>
  )
}

export default AnalyticsCard
